// Cards play their clip muted on hover (docs/brand/BRAND.md, "Images and video first"). A card that has a clip
// spreads `hover.handlers` on itself and puts `hover.layer` inside its picture frame (a positioned box): after a
// short beat of a mouse resting on the card the clip loads, plays muted and fades in over the still; leaving
// pauses it and fades back to the still. Only for a mouse; never under reduced motion or data saver, where the
// still stays. CSS: ui/motion.css (`.hc-clip`).
import { type PointerEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { useAssetUrl } from "../useAsset.ts";
import { useApp } from "./app.ts";

/** how long a mouse rests on a card before its clip starts: passing over a grid plays nothing */
const INTENT_MS = 140;
/** after leaving, how long the paused clip stays mounted (a quick return resumes it) */
const LINGER_MS = 1500;

// The event itself says it is a mouse (a tablet with a mouse attached counts); only the person's settings can
// keep the still.
function mayPlay(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return false;
  const conn = (navigator as { connection?: { saveData?: boolean } }).connection;
  return !conn?.saveData;
}

export interface HoverClip {
  handlers: {
    onPointerEnter: (e: PointerEvent) => void;
    onPointerLeave: () => void;
  };
  /** the clip over the still, or nothing while the card was never hovered */
  layer: ReactNode;
  /** the clip is on screen and playing */
  playing: boolean;
}

/** `clip`: the asset version of a video (its proxy plays when there is one); null = a card without a clip. */
export function useHoverClip(clip: string | null | undefined): HoverClip {
  const [mounted, setMounted] = useState(false);
  const [want, setWant] = useState(false);
  const [playing, setPlaying] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const handlers = {
    onPointerEnter: (e: PointerEvent) => {
      if (!clip || e.pointerType !== "mouse" || !mayPlay()) return;
      clear();
      timer.current = setTimeout(() => {
        setMounted(true);
        setWant(true);
      }, INTENT_MS);
    },
    onPointerLeave: () => {
      clear();
      setWant(false);
      setPlaying(false);
      timer.current = setTimeout(() => setMounted(false), LINGER_MS);
    },
  };
  const layer =
    clip && mounted ? <ClipLayer clip={clip} want={want} onPlaying={() => setPlaying(true)} /> : null;
  return { handlers, layer, playing: playing && want };
}

function ClipLayer({ clip, want, onPlaying }: { clip: string; want: boolean; onPlaying: () => void }) {
  const { api, ws } = useApp();
  const { url } = useAssetUrl(api, ws, clip);
  const ref = useRef<HTMLVideoElement>(null);
  const [live, setLive] = useState(false);
  const src = url ? (url.proxy_url ?? url.url) : null;
  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return;
    if (want) v.play().catch(() => {});
    else v.pause();
  }, [want, src]);
  // unmounted (left a while ago, or the card went away): stop the download too, not only the picture. The
  // element is kept here because React clears `ref` before this cleanup runs.
  const node = useRef<HTMLVideoElement | null>(null);
  useEffect(
    () => () => {
      const v = node.current;
      if (!v) return;
      v.pause();
      v.removeAttribute("src");
      v.load();
    },
    [],
  );
  if (!src) return null;
  return (
    <video
      ref={(el) => {
        ref.current = el;
        if (el) node.current = el;
      }}
      className={`hc-clip${live && want ? " on" : ""}`}
      src={src}
      muted
      loop
      playsInline
      preload="auto"
      aria-hidden="true"
      tabIndex={-1}
      disablePictureInPicture
      onPlaying={() => {
        setLive(true);
        onPlaying();
      }}
    />
  );
}
